import { Dispatch, useState } from "react";
import { useGoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { useDispatch, useSelector } from "react-redux";
import * as Actions from "../../store/actions";
import { useModal } from "../../Sliders/Modal/ModalContext";
import { useLocation } from "react-router-dom";

const ORANGE = "#FF6A1A";
const NAVY = "#101535";

const inputBase = {
  width: "100%",
  height: 52,
  background: "#fff",
  border: "1.5px solid #E7E0D5",
  borderRadius: 12,
  padding: "0 16px",
  fontSize: 16,
  fontWeight: 500,
  color: NAVY,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box" as const,
};

const First = ({ currPage, setCurrPage, modalId }: any) => {
  const dispatch: Dispatch<any> = useDispatch();
  const { hideModal } = useModal();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const loading = useSelector((state: any) => state.client.loading);
  const isAuth = useSelector((state: any) => state.client.isAuth);

  const [email, setEmail] = useState("");
  const [focused, setFocused] = useState(false);

  // The backend resolves the profile from Google itself, so only the raw
  // credential is sent: One Tap returns a signed id token, the button flow an
  // access token.
  const handleLoginSuccess = (tokenResponse: any) => {
    const userInfo: any = tokenResponse.credential
      ? { provider: "google", credential: tokenResponse.credential }
      : {
          provider: "google",
          accessToken: tokenResponse.access_token,
          ...(tokenResponse.expires_in ? { expiresIn: tokenResponse.expires_in } : {}),
        };
    if (query.get("refCode")) userInfo.refCode = query.get("refCode");
    dispatch(Actions.joinSocialClient(userInfo, successCallback, errorCallback));
  };

  const successCallback = (data: any) => {
    if (!data?.client.email || !data?.client.fullname) {
      setCurrPage(3);
    } else {
      setCurrPage(1);
      hideModal(modalId);
    }
  };

  const errorCallback = () => {};

  const login = useGoogleLogin({
    onSuccess: handleLoginSuccess,
    onError: () => console.log("Login Failed"),
  });

  useGoogleOneTapLogin({
    disabled: isAuth,
    onSuccess: handleLoginSuccess,
    onError: () => console.log("One Tap Login Failed"),
  });

  const handleSubmit = () => {
    if (!email.trim()) return;
    let x: any = { email };
    if (query.get("refCode")) x = { ...x, refCode: query.get("refCode") };
    dispatch(Actions.joinClient(x, () => setCurrPage(currPage + 1), () => {}));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Heading */}
      <div style={{ marginBottom: 28, marginTop: 4 }}>
        <h2
          style={{
            fontFamily: '"Bricolage Grotesque", sans-serif',
            fontSize: 26,
            fontWeight: 700,
            color: NAVY,
            letterSpacing: -0.5,
            marginBottom: 6,
          }}
        >
          Get started
        </h2>
        <p style={{ color: "#6C7088", fontSize: 14, lineHeight: 1.6 }}>
          Enter your email to sign in or create an account.
        </p>
      </div>

      {/* Email field */}
      <div style={{ marginBottom: 14 }}>
        <label
          style={{
            display: "block",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".3px",
            textTransform: "uppercase",
            color: "#6C7088",
            marginBottom: 7,
          }}
        >
          Email address
        </label>
        <input
          type="email"
          value={email}
          placeholder="you@example.com"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...inputBase,
            borderColor: focused ? ORANGE : "#E7E0D5",
            boxShadow: focused ? "0 0 0 3px rgba(255,106,26,.1)" : "none",
          }}
        />
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !email.trim()}
        style={{
          width: "100%",
          height: 52,
          background: loading || !email.trim() ? "#E8C4A8" : ORANGE,
          color: "#fff",
          border: "none",
          borderRadius: 13,
          fontWeight: 700,
          fontSize: 15,
          cursor: loading || !email.trim() ? "not-allowed" : "pointer",
          fontFamily: "inherit",
          boxShadow: loading || !email.trim() ? "none" : "0 6px 20px -6px rgba(255,106,26,.5)",
          transition: "background .2s",
          marginBottom: 20,
        }}
        onMouseOver={(e) => { if (!loading && email.trim()) (e.currentTarget as HTMLElement).style.background = "#E8550B"; }}
        onMouseOut={(e) => { if (!loading && email.trim()) (e.currentTarget as HTMLElement).style.background = ORANGE; }}
      >
        {loading ? "Loading…" : "Continue"}
      </button>

      {/* Divider */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
        }}
      >
        <div style={{ flex: 1, height: 1, background: "#E7E0D5" }} />
        <span style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600 }}>or</span>
        <div style={{ flex: 1, height: 1, background: "#E7E0D5" }} />
      </div>

      {/* Google */}
      <button
        type="button"
        onClick={() => login()}
        style={{
          width: "100%",
          height: 52,
          background: "#fff",
          border: "1.5px solid #E7E0D5",
          borderRadius: 13,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          fontWeight: 600,
          fontSize: 14,
          color: NAVY,
          cursor: "pointer",
          fontFamily: "inherit",
          transition: "background .18s, border-color .18s",
        }}
        onMouseOver={(e) => {
          (e.currentTarget as HTMLElement).style.background = "#F5F0EA";
          (e.currentTarget as HTMLElement).style.borderColor = "#D4CCC4";
        }}
        onMouseOut={(e) => {
          (e.currentTarget as HTMLElement).style.background = "#fff";
          (e.currentTarget as HTMLElement).style.borderColor = "#E7E0D5";
        }}
      >
        <img src="/google_single.png" width={20} alt="Google" />
        Continue with Google
      </button>

      <p className="mt-auto pt-8" style={{ fontSize: 12, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center" }}>
        By continuing, you agree to Stavigo's{" "}
        <a href="/terms" target="_blank" style={{ color: ORANGE, textDecoration: "underline" }}>Terms</a>{" "}
        and{" "}
        <a href="/policy" target="_blank" style={{ color: ORANGE, textDecoration: "underline" }}>Privacy Policy</a>.
      </p>
    </div>
  );
};

export default First;
